/**
 * =============================================================================
 * Util: affiliate-csv-utils
 * Versão: 1.0.0
 *
 * Descrição:
 *   Parser e validações de CSV específicas para a importação em massa de
 *   afiliados. Reaproveita os utilitários genéricos de `csv-utils.ts`
 *   (`parseCSVLine`, `detectDelimiter`) mas define colunas obrigatórias
 *   próprias do domínio "afiliado" (nome + e-mail) — diferentemente do
 *   importador de listas de campanha, que exige "telefone".
 * =============================================================================
 */
import { detectDelimiter, parseCSVLine } from './csv-utils';

// =============================================================================
// TIPOS
// =============================================================================

/** Linha bruta de CSV após parse (chave = header normalizado). */
export interface AffiliateCSVRow {
  [key: string]: string;
}

/** Resultado do parse completo. */
export interface AffiliateCSVParseResult {
  headers: string[];
  rows: AffiliateCSVRow[];
  totalRows: number;
  errors: string[];
}

/** Tipo de documento aceito pelo back-end. */
export type AffiliateDocumentType = 'cpf' | 'cnpj';

/** Validação por linha (consumida pelo step de validação). */
export interface AffiliateRowValidation {
  rowIndex: number;
  row: AffiliateCSVRow;
  isValid: boolean;
  errors: string[];
  /** Telefone normalizado (E.164 sem '+'), quando informado. */
  phoneNormalized?: string;
  /** E-mail normalizado (trim + lowercase). */
  emailNormalized?: string;
  /** Documento somente dígitos (sem máscara). */
  documentNormalized?: string;
  documentType?: AffiliateDocumentType | null;
  /** True se o e-mail aparece duplicado dentro do CSV. */
  isDuplicateInCSV?: boolean;
  /** Índice da primeira ocorrência (quando duplicada). */
  duplicateOfIndex?: number;
  /** True se o e-mail já existe em `affiliates` no tenant ativo. */
  existsInSystem?: boolean;
  /** id do afiliado existente (quando aplicável). */
  existingAffiliateId?: string;
}

// =============================================================================
// CONSTANTES — colunas obrigatórias e aliases aceitos
// =============================================================================

export const AFFILIATE_REQUIRED_COLUMNS = ['nome', 'email'] as const;

/**
 * Aliases aceitos para cada coluna canônica.
 * Tudo é comparado em lowercase, sem acento e sem espaço.
 */
const COLUMN_ALIASES: Record<string, string[]> = {
  nome: ['nome', 'name', 'nomecompleto', 'fullname', 'full_name'],
  email: ['email', 'e-mail', 'mail'],
  telefone: ['telefone', 'phone', 'celular', 'whatsapp', 'cel'],
  documento: ['documento', 'cpf', 'cnpj', 'cpf_cnpj', 'cpfcnpj', 'doc'],
  tipo_documento: ['tipo_documento', 'tipodocumento', 'document_type', 'tipo_doc', 'tipo'],
  observacoes: ['observacoes', 'observação', 'observacao', 'notes', 'obs', 'notas'],
};

/** Normaliza um header (lowercase, sem acentos, sem espaços/underline). */
function canonicalizeHeader(raw: string): string {
  return raw
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[\s_-]/g, '')
    .trim();
}

/**
 * Mapeia headers reais do CSV para os nomes canônicos do nosso modelo.
 * Retorna um Map<headerOriginal, nomeCanônico>.
 */
function buildHeaderMap(rawHeaders: string[]): Map<string, string> {
  const map = new Map<string, string>();
  const aliasIndex = new Map<string, string>();
  Object.entries(COLUMN_ALIASES).forEach(([canonical, aliases]) => {
    aliases.forEach((a) => aliasIndex.set(canonicalizeHeader(a), canonical));
  });

  rawHeaders.forEach((h) => {
    const canon = aliasIndex.get(canonicalizeHeader(h));
    if (canon) map.set(h, canon);
  });

  return map;
}

// =============================================================================
// PARSER
// =============================================================================

/**
 * Faz o parse de um CSV de afiliados.
 * - Auto-detecta delimitador (',' ou ';').
 * - Mapeia variações de header (e-mail/email, nome/name etc.).
 * - Valida presença das colunas obrigatórias (`nome` e `email`).
 */
export function parseAffiliateCSV(content: string): AffiliateCSVParseResult {
  const errors: string[] = [];
  const lines = content.trim().split(/\r?\n/);

  if (lines.length < 2) {
    return { headers: [], rows: [], totalRows: 0, errors: ['Arquivo CSV vazio ou sem dados'] };
  }

  const delimiter = detectDelimiter(lines[0]);
  const rawHeaders = parseCSVLine(lines[0], delimiter).map((h) => h.trim());
  const headerMap = buildHeaderMap(rawHeaders);
  const canonicalHeaders = Array.from(headerMap.values());

  // Valida colunas obrigatórias
  for (const required of AFFILIATE_REQUIRED_COLUMNS) {
    if (!canonicalHeaders.includes(required)) {
      errors.push(`Coluna obrigatória "${required}" não encontrada`);
    }
  }
  if (errors.length > 0) {
    return { headers: canonicalHeaders, rows: [], totalRows: 0, errors };
  }

  const rows: AffiliateCSVRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    const values = parseCSVLine(line, delimiter);
    const row: AffiliateCSVRow = {};
    rawHeaders.forEach((rawHeader, idx) => {
      const canon = headerMap.get(rawHeader);
      if (!canon) return; // Ignora colunas desconhecidas
      row[canon] = (values[idx] ?? '').trim();
    });
    rows.push(row);
  }

  return { headers: canonicalHeaders, rows, totalRows: rows.length, errors };
}

// =============================================================================
// VALIDAÇÃO DE CAMPOS
// =============================================================================

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(value: string): boolean {
  if (!value) return false;
  if (value.length > 255) return false;
  return EMAIL_REGEX.test(value);
}

/**
 * Normaliza telefone para E.164 (apenas dígitos), adicionando o DDI default
 * caso ele esteja ausente (mesma lógica do importador de listas).
 */
export function normalizeAffiliatePhoneInput(
  phone: string,
  defaultCountryCode = '55',
): { normalized: string; valid: boolean; error?: string } {
  const digits = (phone || '').replace(/\D/g, '');
  if (!digits) return { normalized: '', valid: true }; // telefone é opcional

  let withDdi = digits;
  if (!digits.startsWith(defaultCountryCode) && digits.length >= 10 && digits.length <= 11) {
    withDdi = defaultCountryCode + digits;
  }

  // Faixa E.164 (8 a 15 dígitos)
  if (withDdi.length < 8 || withDdi.length > 15) {
    return { normalized: withDdi, valid: false, error: 'Telefone inválido' };
  }
  return { normalized: withDdi, valid: true };
}

/** Detecta tipo de documento por contagem de dígitos. */
export function detectDocumentType(value: string): AffiliateDocumentType | null {
  const digits = (value || '').replace(/\D/g, '');
  if (digits.length === 11) return 'cpf';
  if (digits.length === 14) return 'cnpj';
  return null;
}

// =============================================================================
// TEMPLATE — download client-side
// =============================================================================

const AFFILIATE_CSV_TEMPLATE = `nome,email,telefone,documento,tipo_documento,observacoes
João da Silva,joao@exemplo.com,5562999998888,12345678900,cpf,Indicado por parceiro
Acme Marketing LTDA,contato@acme.com,5511988887777,12345678000190,cnpj,Agência parceira`;

export function downloadAffiliateCSVTemplate() {
  const blob = new Blob([AFFILIATE_CSV_TEMPLATE], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', 'modelo_afiliados.csv');
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
